import React, { useState, useEffect } from 'react';
import { homeworkService } from '../../lib/services/homework';
import ClassManagement from './ClassManagement';
import TeacherManagement from './TeacherManagement';
import StudentManagement from './StudentManagement';
import TimetableSystem from './TimetableSystem';
import HomeworkCalendar from './HomeworkCalendar';
import SearchFilter from './SearchFilter';
import StatisticsPanel from './StatisticsPanel';
import './HomeworkModule.css';

const HomeworkModule = ({ currentUser, onBack, onTabChange, viewOnly = false, role = 'parent' }) => {
  // Remove or disable homework creation if viewOnly
  const canCreateHomework = !viewOnly && role !== 'parent';
  
  const [currentView, setCurrentView] = useState(role === 'parent' ? 'homework-list' : 'classes'); // 'classes', 'teachers', 'students', 'homework-list'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTimetable, setShowTimetable] = useState(false);
  const [showHomeworkCalendar, setShowHomeworkCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [homeworkList, setHomeworkList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  // Time slots and day names
  const timeSlots = [
    '8:10-8:45', '8:45-9:20', '9:20-9:55', '10:15-10:50', 
    '10:50-11:25', '11:30-12:05', '12:05-12:40', '1:50-2:25', 
    '2:25-3:00', '3:00-3:35'
  ];

  const dayNames = ['星期一', '星期二', '星期三', '星期四', '星期五'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  // Load initial data
  useEffect(() => {
    if (role === 'parent' && currentUser?.children) {
      // Filter homework to show only children's homework
      fetchChildrenHomework(currentUser.children);
    } else {
      loadInitialData();
    }
  }, [currentUser, role]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [classes, teachers, students, statistics] = await Promise.all([
        homeworkService.getClasses(),
        homeworkService.getTeachers(),
        homeworkService.getStudents(),
        homeworkService.getHomeworkStats()
      ]);

      setFilteredClasses(classes);
      setFilteredTeachers(teachers);
      setFilteredStudents(students);
      setStats(statistics);
    } catch (err) {
      console.error('Error loading homework data:', err);
      setError('載入家課管理資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  // Fetch homework for children (parent view)
  const fetchChildrenHomework = async (children) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get homework for all children
      const childIds = children.map(child => child.id || child.student_id);
      const allHomework = [];
      
      for (const childId of childIds) {
        try {
          const homework = await homeworkService.getHomeworkByStudent(childId);
          if (homework && Array.isArray(homework)) {
            allHomework.push(...homework);
          }
        } catch (err) {
          console.warn(`Error fetching homework for child ${childId}:`, err);
        }
      }
      
      setHomeworkList(allHomework);
    } catch (err) {
      console.error('Error loading children homework:', err);
      setError('載入子女家課時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowTimetable(true);
    setShowHomeworkCalendar(false);
  };

  const handleTimetableClick = (timeSlot, day) => {
    if (selectedItem) {
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setShowHomeworkCalendar(true);
    }
  };

  const handleBackToList = () => {
    setShowTimetable(false);
    setShowHomeworkCalendar(false);
    setSelectedItem(null);
    setSelectedDate(null);
  };

  const handleBackToTimetable = () => {
    setShowHomeworkCalendar(false);
    setSelectedDate(null);
  };

  if (loading) {
    return (
      <div className="homework-module">
        <div className="homework-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>家課管理系統</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入家課管理中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="homework-module">
        <div className="homework-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>家課管理系統</h1>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadInitialData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="homework-module">
      <div className="homework-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>{role === 'parent' ? '家課' : '家課管理系統'}</h1>
        {!viewOnly && canCreateHomework && !showTimetable && !showHomeworkCalendar && (
          <div className="homework-nav">
            <button 
              className={`nav-btn ${currentView === 'classes' ? 'active' : ''}`}
              onClick={() => setCurrentView('classes')}
            >
              🏛️ 班級管理
            </button>
            <button 
              className={`nav-btn ${currentView === 'teachers' ? 'active' : ''}`}
              onClick={() => setCurrentView('teachers')}
            >
              👨‍🏫 教師管理
            </button>
            <button 
              className={`nav-btn ${currentView === 'students' ? 'active' : ''}`}
              onClick={() => setCurrentView('students')}
            >
              👨‍🎓 學生管理
            </button>
          </div>
        )}
        {viewOnly && role === 'parent' && (
          <div className="homework-info">
            <p>查看子女的家課</p>
          </div>
        )}
      </div>

      <div className="homework-content">
        {!showTimetable && !showHomeworkCalendar && (
          <>
            {!viewOnly && canCreateHomework && (
              <>
                <StatisticsPanel stats={stats} />
                
                <SearchFilter
                  data={currentView === 'classes' ? filteredClasses : 
                        currentView === 'teachers' ? filteredTeachers : filteredStudents}
                  onFilter={currentView === 'classes' ? setFilteredClasses : 
                           currentView === 'teachers' ? setFilteredTeachers : setFilteredStudents}
                  type={currentView.slice(0, -1)} // Remove 's' from end
                  placeholder={`搜尋${currentView === 'classes' ? '班級' : 
                              currentView === 'teachers' ? '教師' : '學生'}...`}
                />

                {currentView === 'classes' && (
                  <ClassManagement 
                    classes={filteredClasses}
                    onClassSelect={handleItemSelect}
                  />
                )}

                {currentView === 'teachers' && (
                  <TeacherManagement 
                    teachers={filteredTeachers}
                    onTeacherSelect={handleItemSelect}
                  />
                )}

                {currentView === 'students' && (
                  <StudentManagement 
                    students={filteredStudents}
                    onStudentSelect={handleItemSelect}
                  />
                )}
              </>
            )}
            
            {viewOnly && role === 'parent' && (
              <div className="homework-list">
                {homeworkList.length > 0 ? (
                  <div className="homework-items">
                    {homeworkList.map((hw, index) => (
                      <div key={index} className="homework-item">
                        <h3>{hw.title || hw.subject || '家課'}</h3>
                        <p>{hw.description || hw.content || ''}</p>
                        <div className="homework-meta">
                          <span>科目: {hw.subject || '未指定'}</span>
                          <span>到期日: {hw.due_date ? new Date(hw.due_date).toLocaleDateString('zh-TW') : '未指定'}</span>
                          <span>狀態: {hw.status || '未完成'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>暫無家課記錄</p>
                )}
              </div>
            )}
          </>
        )}

        {showTimetable && !showHomeworkCalendar && (
          <TimetableSystem
            selectedItem={selectedItem}
            timeSlots={timeSlots}
            dayNames={dayNames}
            dayKeys={dayKeys}
            onTimetableClick={handleTimetableClick}
            onBack={handleBackToList}
          />
        )}

        {showHomeworkCalendar && (
          <HomeworkCalendar
            selectedItem={selectedItem}
            selectedDate={selectedDate}
            onBack={handleBackToTimetable}
          />
        )}
      </div>
    </div>
  );
};

export default HomeworkModule;
